// src/components/SubMenuToolbar.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Tooltip,
  Divider,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  Deselect as DeselectIcon,
  AutoAwesome as AutoAwesomeIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";

import BreadcrumbsComponent from "./Breadcrumbs";
import DesignHelpPopover from "./DesignHelpPopover";
import SharePopover from "./SharePopover";
import { useData } from "../hooks/useData";
import { useMediaQuery } from "@mui/material";

const SubMenuToolbar = () => {
  const { canvas, setSnackbar } = useData();
  const isMobile = useMediaQuery("(max-width:767px)");

  // BUTTON STATES
  const [isTextActionDisabled, setIsTextActionDisabled] = useState(true);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [isDeselectDisabled, setIsDeselectDisabled] = useState(true);
  const [isDesignHelpDisabled, setIsDesignHelpDisabled] = useState(true);
  const [isShareDisabled, setIsShareDisabled] = useState(true);

  // POPOVER STATES
  const [showDesignHelpPopover, setShowDesignHelpPopover] = useState(false);
  const [showSharePopover, setShowSharePopover] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // CANVAS LISTENERS
  useEffect(() => {
    if (!canvas) return;

    const updateStates = () => {
      const active = canvas.getActiveObject();
      const hasObjects = canvas.getObjects().length > 0;
      const isText = active?.type === "i-text";

      setIsTextActionDisabled(!isText);
      setIsDeleteDisabled(!isText);
      setIsDeselectDisabled(!isText);
      setIsDesignHelpDisabled(!hasObjects);
      setIsShareDisabled(!hasObjects);
    };

    canvas.on({
      "selection:created": updateStates,
      "selection:updated": updateStates,
      "selection:cleared": updateStates,
      "object:added": updateStates,
      "object:removed": updateStates,
    });

    updateStates();

    return () => {
      canvas.off({
        "selection:created": updateStates,
        "selection:updated": updateStates,
        "selection:cleared": updateStates,
        "object:added": updateStates,
        "object:removed": updateStates,
      });
    };
  }, [canvas]);

  // ACTION HANDLERS
  const handleDelete = () => {
    const obj = canvas.getActiveObject();
    if (obj?.type === "i-text") {
      canvas.remove(obj);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      setSnackbar({
        open: true,
        message: "Text deleted.",
        severity: "success",
      });
    }
  };

  const handleCenter = () => {
    const obj = canvas.getActiveObject();
    if (obj?.type === "i-text") {
      obj.set({ left: canvas.getWidth() / 2, originX: "center" });
      obj.setCoords();
      canvas.requestRenderAll();
    }
  };

  const handleDeselect = () => {
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: { xs: 0.5, sm: 1 },
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* BREADCRUMBS — HIDDEN ON MOBILE */}
      <Box
        sx={{
          ml: { xs: 1, sm: 2, md: 12 },
          display: { xs: "none", sm: "block" },
        }}
      >
        <BreadcrumbsComponent />
      </Box>

      {/* TOOLBAR */}
      <Stack direction="row" spacing={2} sx={{ mr: { xs: 1, sm: 2, md: 12 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* ALWAYS VISIBLE */}
          <Tooltip title="Deselect all objects">
            <span>
              <Button
                onClick={handleDeselect}
                disabled={isDeselectDisabled}
                variant="outlined"
                size="small"
                startIcon={<DeselectIcon />}
                sx={{ textTransform: "none", minWidth: 64 }}
              >
                Deselect
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Center selected text">
            <span>
              <Button
                onClick={handleCenter}
                disabled={isDeleteDisabled}
                variant="outlined"
                size="small"
                startIcon={<FormatAlignCenterIcon />}
                sx={{ textTransform: "none", minWidth: 64 }}
              >
                Center
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Delete selected text">
            <span>
              <Button
                onClick={handleDelete}
                disabled={isDeleteDisabled}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                sx={{ textTransform: "none", minWidth: 64 }}
              >
                Delete
              </Button>
            </span>
          </Tooltip>

          {/* MOBILE: COLLAPSE INTO MORE MENU */}
          {isMobile ? (
            <>
              <IconButton onClick={handleMenuOpen} size="small">
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { mt: 1 } }}
              >
                <MenuItem
                  onClick={() => {
                    setShowDesignHelpPopover(true);
                    handleMenuClose();
                  }}
                  disabled={isDesignHelpDisabled}
                >
                  <ListItemIcon>
                    <AutoAwesomeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Design Help</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setShowSharePopover(true);
                    handleMenuClose();
                  }}
                  disabled={isShareDisabled}
                >
                  <ListItemIcon>
                    <GroupsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Share</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Request professional design help">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    disabled={isDesignHelpDisabled}
                    onClick={() => setShowDesignHelpPopover(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Design Help
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Share your design">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GroupsOutlinedIcon />}
                    disabled={isShareDisabled}
                    onClick={() => setShowSharePopover(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Share
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Stack>

      {/* POPOVERS */}
      <DesignHelpPopover
        canvas={canvas}
        open={showDesignHelpPopover}
        onClose={() => setShowDesignHelpPopover(false)}
      />
      <SharePopover
        canvas={canvas}
        open={showSharePopover}
        onClose={() => setShowSharePopover(false)}
      />
    </Box>
  );
};

export default SubMenuToolbar;
